import { CloseOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Select } from 'antd';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import ContentCard from '../../components/ContentCard';
import CourseOption from '../../components/CourseOption';
import SearchLoadingIndicator from '../../components/SearchLoadingIndicator';
import UserOption from '../../components/UserOption';
import Course from '../../course/types/Course';
import User from '../../user/types/User';
import useInstructorOptions from '../../user/hooks/useInstructorOptions';
import { Permission } from '../../user/types/Permission';
import { useUser } from '../../user/userContext';
import { userHasPermission } from '../../user/userUtils';
import { getSearchParam, stringifyValues } from '../../utils/searchParams';
import { OpenClassStatus } from '../types/OpenClass';
import { OpenClassFilterValues } from '../utils/openClassFilter';
import OpenClassStatusBadge from './OpenClassStatusBadge';

type FilterFormValues = {
  courseIds?: Course['id'][];
  endsAt?: dayjs.Dayjs;
  instructorIds?: User['id'][];
  startsAt?: dayjs.Dayjs;
  status?: OpenClassStatus[];
};

type OpenClassFilterProps = {
  courses: Course[];
  visible: boolean;
  onChange: (filter: OpenClassFilterValues, pristine: boolean) => void;
};

const OpenClassFilter: React.FC<OpenClassFilterProps> = ({
  courses,
  visible,
  onChange,
}) => {
  const [form] = Form.useForm();
  const selectedInstructorIds = Form.useWatch('instructorIds', form);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [currentUser] = useUser();
  const { t } = useTranslation();
  const canReadInstructors = userHasPermission(
    currentUser,
    Permission.USER_READ_LIST,
  );

  const {
    hasMoreInstructors,
    instructors,
    loadFirstInstructorPage,
    loadMoreInstructors,
    loadingInstructorOptions,
    loadingInstructors,
    searchInstructors,
  } = useInstructorOptions({
    enabled: visible && canReadInstructors,
    hydrateSelectedIds: selectedInstructorIds,
  });

  const defaultFilter = useMemo<FilterFormValues>(
    () => ({
      courseIds: undefined,
      endsAt: undefined,
      instructorIds: undefined,
      startsAt: dayjs().startOf('day'),
      status: undefined,
    }),
    [],
  );

  const applyFilterValues = useCallback(
    (values: FilterFormValues) => {
      setSearchParams(
        stringifyValues({
          ...values,
          startsAt: values.startsAt?.format('YYYY-MM-DD'),
          endsAt: values.endsAt?.format('YYYY-MM-DD'),
        }),
        { replace: true },
      );
      onChange(
        {
          courseIds: values.courseIds,
          endsAt: values.endsAt?.endOf('day').toISOString(),
          instructorIds: values.instructorIds,
          startsAt: values.startsAt?.startOf('day').toISOString(),
          status: values.status,
        },
        isEqual(values, defaultFilter),
      );
    },
    [defaultFilter, onChange, setSearchParams],
  );

  // Read once on mount so a shared link opens on the same selection.
  useEffect(() => {
    if (location.search) {
      form.setFieldsValue({
        courseIds: getSearchParam(searchParams, 'courseIds', 'list')?.map(
          Number,
        ),
        endsAt: getSearchParam(searchParams, 'endsAt', 'moment'),
        instructorIds: getSearchParam(
          searchParams,
          'instructorIds',
          'list',
        )?.map(Number),
        startsAt: getSearchParam(searchParams, 'startsAt', 'moment'),
        status: getSearchParam<OpenClassStatus>(searchParams, 'status', 'list'),
      });
    }
    applyFilterValues(form.getFieldsValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isEmpty = useCallback(
    () => Object.values(form.getFieldsValue()).filter(Boolean).length === 0,
    [form],
  );

  const clearFilter = useCallback(() => {
    const emptyFilter = Object.keys(form.getFieldsValue()).reduce(
      (values, key) => ({ ...values, [key]: undefined }),
      {},
    );
    form.setFieldsValue(emptyFilter);
    applyFilterValues(emptyFilter);
  }, [applyFilterValues, form]);

  return (
    <ContentCard className={`flex flex-col ${!visible ? 'hidden' : ''}`}>
      <Button
        className="mb-2 self-end"
        disabled={isEmpty()}
        icon={<CloseOutlined />}
        type="text"
        onClick={clearFilter}>
        {t('common.clear')}
      </Button>
      <Form
        form={form}
        initialValues={defaultFilter}
        layout="vertical"
        wrapperCol={{ span: 18 }}
        onValuesChange={(_, values) =>
          applyFilterValues(values as FilterFormValues)
        }>
        <div className="grid-flow-col grid-cols-2 grid-rows-3 gap-x-4 md:grid">
          <Form.Item label={t('common.instructors')} name="instructorIds">
            <Select
              allowClear
              disabled={!canReadInstructors}
              filterOption={false}
              loading={loadingInstructors}
              mode="multiple"
              notFoundContent={
                loadingInstructorOptions ? <SearchLoadingIndicator /> : null
              }
              showSearch
              onDropdownVisibleChange={(open) => {
                if (open) {
                  loadFirstInstructorPage('');
                }
              }}
              onPopupScroll={(event) => {
                const target = event.target as HTMLDivElement;
                const isNearBottom =
                  target.scrollTop + target.offsetHeight >=
                  target.scrollHeight - 32;

                if (isNearBottom && hasMoreInstructors) {
                  loadMoreInstructors();
                }
              }}
              onSearch={searchInstructors}>
              {instructors.map(
                (instructor) =>
                  instructor.id && (
                    <Select.Option key={instructor.id} value={instructor.id}>
                      <UserOption currentUser={currentUser} user={instructor} />
                    </Select.Option>
                  ),
              )}
            </Select>
          </Form.Item>
          <Form.Item label={t('common.courses')} name="courseIds">
            <Select
              allowClear
              filterOption={(input, option) =>
                option?.props.children.props.course.name
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              mode="multiple">
              {courses.map((course) => (
                <Select.Option key={course.id} value={course.id}>
                  <CourseOption course={course} user={currentUser} />
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label={t('common.status')} name="status">
            <Select allowClear mode="multiple">
              {[
                OpenClassStatus.Confirmed,
                OpenClassStatus.Preliminary,
                OpenClassStatus.Ordered,
                OpenClassStatus.Canceled,
              ].map((status) => (
                <Select.Option key={status} value={status}>
                  <OpenClassStatusBadge status={status} />
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label={t('views.OpenClasses.from')} name="startsAt">
            <DatePicker
              className="w-full"
              disabledDate={(date) => {
                const endsAt = form.getFieldValue('endsAt');
                return !!endsAt && date.isAfter(endsAt, 'day');
              }}
              format="YYYY-MM-DD"
              size="large"
            />
          </Form.Item>
          <Form.Item label={t('views.OpenClasses.to')} name="endsAt">
            <DatePicker
              className="w-full"
              disabledDate={(date) => {
                const startsAt = form.getFieldValue('startsAt');
                return !!startsAt && date.isBefore(startsAt, 'day');
              }}
              format="YYYY-MM-DD"
              size="large"
            />
          </Form.Item>
        </div>
      </Form>
    </ContentCard>
  );
};

export default OpenClassFilter;
